import React, { useMemo } from 'react';
import type { TextProps } from '../Text/Text';
import Text from '../Text/Text';
import moment from 'moment';
import 'moment-timezone';
import { nonWeakMemoize3 } from '@seaber/common/lib/memoize';
import { normalizeTimezone } from '../../../lib/moment-timezone-utils';

interface TimeFieldProps extends TextProps {
  value: string | number | undefined | null;
  timeZone?: string;
  formatstring?: string;
}

export const timeFieldFormat = nonWeakMemoize3(
  (
    value: string | number,
    timeZone: string | undefined = 'UTC',
    formatstring: string | undefined = 'D.M.yyyy HH:mm (z)',
  ): string => {
    const normalizedTimezone = normalizeTimezone(timeZone);
    try {
      return moment(value).tz(normalizedTimezone).format(formatstring);
    } catch (error) {
      console.warn(
        `Invalid timezone "${normalizedTimezone}" (original: "${timeZone}"), falling back to UTC`,
        error,
      );
      return moment(value).tz('UTC').format(formatstring);
    }
  },
);

const TimeField = ({
  value,
  timeZone,
  formatstring,
  ...restProps
}: TimeFieldProps): JSX.Element | null => {
  const text = useMemo(
    () => value && timeFieldFormat(value, timeZone, formatstring),
    [value, timeZone, formatstring],
  );

  return text ? <Text {...restProps}>{text}</Text> : null;
};

export default TimeField;
